@if (data?.media?.mediaType === MediaType.Audio) {
	<audio
		#audioPlayer
		(canplay)="audioLoaded = true"
		(error)="audioError = true"
		(loadedmetadata)="audioMetaDataLoaded()"
		(timeupdate)="timeUpdateAudio($event)"
		hidden
	></audio>
}

<sim-basic-material-dialog
	(closeClicked)="close()"
	[finished]="finished"
	[headerIcon]="getHeaderIcon()"
	[headerText]="
		T.EDIT_MEDIA_DIALOG_TITLE
			| translate
				: {
						type: (mediaTypeKey | translate),
						name: data?.media?.name ?? '',
				  }
	"
	[matDialogData]="data"
	[rect]="rect"
	class="normalDuotoneHeaderIcon"
>
	<ng-container dialogContent>
		<form [formGroup]="mediaInformationFormGroup">
			<mat-form-field class="full-width-form" color="accent">
				<mat-label>{{ T.EDIT_MEDIA_DIALOG_LABEL_FILE_NAME | translate }}</mat-label>
				<input formControlName="fileName" matInput type="text" />
				@if (mediaInformationFormGroup.get("fileName").hasError("required")) {
					<mat-error>
						{{ T.EDIT_MEDIA_DIALOG_ERROR_MESSAGE | translate }}
					</mat-error>
				}
			</mat-form-field>

			<mat-form-field class="full-width-form" color="accent">
				<mat-label>{{ T.EDIT_MEDIA_DIALOG_LABEL_FILE_PATH | translate }}</mat-label>
				<input formControlName="filePath" matInput />
			</mat-form-field>

			@if (data?.media?.mediaType === MediaType.Audio) {
				<div class="sliderFormField full-width-form">
					<label>{{ T.EDIT_MEDIA_DIALOG_LABEL_ROOM | translate }}</label>
					<mat-slider [max]="1" [min]="0" [step]="0.1" color="accent" discrete>
						<input formControlName="roomVolume" matSliderThumb />
					</mat-slider>
				</div>
			}
		</form>

		@if (data?.media?.mediaType === MediaType.Image) {
			<div [style.backgroundImage]="'url(' + simUtilityService.getMediaPath(data.media) + ')'" class="imagePreview">
				@if (imageLoadError) {
					<div class="errorLoading">
						<i class="fad fa-file-image fa-3x"></i>
						<div class="text">
							{{ T.EDIT_MEDIA_DIALOG_IMAGE_LOAD_ERROR | translate }}
						</div>
					</div>
				}
				<img
					(error)="imageLoadError = true"
					[src]="simUtilityService.getMediaPath(data.media)"
					style="visibility: hidden"
					width="100%"
				/>
				<button
					(click)="enlargeImage()"
					[disabled]="imageLoadError"
					color="accent"
					mat-mini-fab
					matTooltip="Show Image in FullScreen"
					matTooltipClass="accent"
				>
					<mat-icon fontIcon="fa-expand-wide" fontSet="fad" inline style="font-size: 1.2em" />
				</button>
			</div>
		}

		@if (data?.media?.mediaType === MediaType.Video) {
			<div [class.videoError]="videoError" class="videoPreview">
				@if (videoError) {
					<div class="errorLoading">
						<i class="fad fa-file-video fa-3x"></i>
						<div class="text">
							{{ T.EDIT_MEDIA_DIALOG_VIDEO_LOAD_ERROR | translate }}
						</div>
					</div>
				}
				<video (error)="videoError = true" [src]="simUtilityService.getMediaPath(data?.media)" controls preload="metadata"></video>
			</div>
		}

		@if (data?.media?.mediaType === MediaType.PDF) {
			<div class="pdfPreview">
				@if (!pdfLoaded && pdfLoadingError) {
					<div class="errorLoading">
						<i class="fad fa-file-pdf fa-3x"></i>
						<div class="text">
							{{ T.EDIT_MEDIA_DIALOG_PDF_LOAD_ERROR | translate }}
						</div>
					</div>
				}
				<sim-component-loading-container
					[isLoading]="!pdfLoaded && !pdfLoadingError"
					[loadingIcon]="'fad fa-file-pdf'"
					[loadingMessage]="T.GLOBAL_TEXT_LOADING | translate"
				/>
				<button
					(click)="openPdfDialog()"
					[disabled]="!pdfLoaded || pdfLoadingError"
					class="controlBtns fullScreenPdfButton"
					color="accent"
					mat-mini-fab
				>
					<mat-icon fontIcon="fa-expand-wide" fontSet="fad" inline style="font-size: 1.2em" />
				</button>
				<sim-button
					(onClick)="openPdfDialog()"
					[disabled]="!pdfLoaded || pdfLoadingError"
					[icon]="{fontSet: 'fad', fontIcon: 'fa-expand-wide'}"
					[label]=""
				/>
				<button
					(click)="pdfPageNum = pdfPageNum - 1"
					[disabled]="!pdfLoaded || pdfLoadingError || pdfPageNum === 1"
					class="controlBtns leftControlBtn"
					color="accent"
					mat-mini-fab
				>
					<mat-icon fontIcon="fa-chevron-left" fontSet="fas" inline style="font-size: 1.2em" />
				</button>
				<sim-document-viewer
					(numberOfPdfPages)="numberOfPdfPages = $event"
					(pdfLoaded)="pdfLoaded = $event"
					(pdfLoadingError)="pdfLoadingError = $event"
					[media]="data?.media"
					[pageNum]="pdfPageNum"
				/>
				<button
					(click)="pdfPageNum = pdfPageNum + 1"
					[disabled]="!pdfLoaded || pdfLoadingError || pdfPageNum === numberOfPdfPages"
					class="controlBtns rightControlBtn"
					color="accent"
					mat-mini-fab
				>
					<mat-icon fontIcon="fa-chevron-right" fontSet="fas" inline style="font-size: 1.2em" />
				</button>
			</div>
		}

		@if (data?.media?.mediaType === MediaType.PowerPoint) {
			<div #con [class.loading]="!powerPointLoaded && !pptxLoadingError" class="powerPointPreview">
				@if (!powerPointLoaded && pptxLoadingError) {
					<div class="errorLoading">
						<i class="fad fa-file-powerpoint fa-3x"></i>
						<div class="text">
							{{ T.EDIT_MEDIA_DIALOG_POWERPOINT_LOAD_ERROR | translate }}
						</div>
					</div>
				}
				<sim-component-loading-container
					[isLoading]="!powerPointLoaded && !pptxLoadingError"
					[loadingIcon]="'fad fa-file-powerpoint'"
					[loadingMessage]="T.GLOBAL_TEXT_LOADING | translate"
				/>
				<button
					(click)="openPowerPointDialog()"
					[disabled]="!powerPointLoaded || pptxLoadingError"
					class="controlBtns fullScreenPdfButton"
					color="accent"
					mat-mini-fab
				>
					<ng-container>
						<mat-icon fontIcon="fa-expand-wide" fontSet="fad" inline style="font-size: 1.2em" />
					</ng-container>
				</button>
				<button
					(click)="powerPointPageNum = powerPointPageNum - 1"
					[disabled]="!powerPointLoaded || pptxLoadingError || powerPointPageNum === 0"
					class="controlBtns leftControlBtn"
					color="accent"
					mat-mini-fab
				>
					<mat-icon fontIcon="fa-chevron-left" fontSet="fas" inline style="font-size: 1.2em" />
				</button>
				<sim-presentation-viewer
					(loaded)="powerPointLoaded = $event"
					(loadingError)="pptxLoadingError = $event"
					(numberOfPages)="numberOfPowerPointPages = $event"
					[height]="200"
					[media]="data?.media"
					[pageNum]="powerPointPageNum"
					[width]="con?.getBoundingClientRect().width"
				/>
				<button
					(click)="powerPointPageNum = powerPointPageNum + 1"
					[disabled]="!powerPointLoaded || pptxLoadingError || powerPointPageNum === numberOfPowerPointPages"
					class="controlBtns rightControlBtn"
					color="accent"
					mat-mini-fab
				>
					<mat-icon fontIcon="fa-chevron-right" fontSet="fas" inline style="font-size: 1.2em" />
				</button>
			</div>
		}

		@if (data?.media?.mediaType === MediaType.Audio) {
			<div [class.audioError]="audioError || !audioLoaded" class="audioPlayer">
				@if (audioError && !audioLoaded) {
					<div class="errorLoading">
						<i class="fad fa-file-audio fa-3x"></i>
						<div class="text">
							{{ T.EDIT_MEDIA_DIALOG_AUDIO_LOAD_ERROR | translate }}
						</div>
					</div>
				}
				<sim-component-loading-container
					[isLoading]="!audioLoaded && !audioError"
					[loadingIcon]="'fad fa-file-audio'"
					[loadingMessage]="T.GLOBAL_TEXT_LOADING | translate"
				/>
				<div class="progressBar">
					<mat-slider color="accent">
						<input [value]="currentProgress" matSliderThumb />
					</mat-slider>
					<div class="duration">
						{{ durationString }}
					</div>
					<div class="currentTime">
						{{ currentTime }}
					</div>
				</div>
				<div class="controlButtons">
					<div class="backward">
						<button color="accent" mat-icon-button>
							<mat-icon fontIcon="fa-step-backward" fontSet="fad" inline />
						</button>
					</div>
					<div [class.mediaIsPlaying]="mediaIsPlaying" class="playPause">
						<button (click)="toggleMediaPlaying()" class="large" color="accent" mat-icon-button>
							<sim-animated-material-icon
								[trigger]="mediaIsPlaying"
								[endIcon]="{
									fontSet: 'fad',
									fontIcon: 'fa-pause',
									themeColor: 'white',
								}"
								[startIcon]="{
									fontSet: 'fad',
									fontIcon: 'fa-play',
									themeColor: 'accent',
								}"
							/>
						</button>
					</div>
					<div class="forward">
						<button color="accent" mat-icon-button>
							<mat-icon fontIcon="fa-step-forward" fontSet="fad" inline />
						</button>
					</div>
				</div>
			</div>
		}
	</ng-container>

	<ng-container actions>
		<button (click)="close()" color="warn" mat-raised-button style="margin-right: 20px">
			{{ T.EDIT_MEDIA_DIALOG_CLOSE_BUTTON | translate }}
		</button>

		<button
			(click)="update()"
			[disabled]="mediaInformationFormGroup.invalid || !mediaInformationFormGroup.dirty"
			color="accent"
			mat-raised-button
		>
			{{ T.EDIT_MEDIA_DIALOG_UPDATE_BUTTON | translate }}
		</button>
	</ng-container>
</sim-basic-material-dialog>
